import { OptionType } from "../components/booking/information/FormSelect";
import { SelectOption } from "../components/ui/SelectWithSearch";
import { ClinicScheduleStatusDto, DoctorDto } from "../web-api-client";
import { normalizeLabel } from "./occupation";

export function convertDobToYMD(dob: string): string {
  const [day, month, year] = dob.split("/");
  return `${year}/${month}/${day}`;
}

export function mapGender(gender: string): string {
  const g = gender.trim().toLowerCase();
  if (g === "nam" || g === "male" || g === "g") return "M";
  if (g === "nữ" || g === "nu" || g === "female" || g === "n") return "F";
  return ""; // fallback
}

export function splitVnAddress(raw: string) {
  // tách theo dấu phẩy; nếu INPUT dùng dấu "/" thì thay bằng phẩy trước
  const parts = (raw || "")
    .replace(/\//g, ",")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return { detail: "", ward: "", district: "", city: "" };
  }

  // Lấy 3 mảnh cuối làm ward/district/city (nếu đủ),
  // phần còn lại ghép lại làm địa chỉ chi tiết
  const cityRaw = parts.length >= 1 ? parts[parts.length - 1] : "";
  const districtRaw = parts.length >= 2 ? parts[parts.length - 2] : "";
  const wardRaw = parts.length >= 3 ? parts[parts.length - 3] : "";

  const detailArr = parts.slice(0, Math.max(0, parts.length - 3));
  const detail = detailArr.join(", ").trim();

  const ward = wardRaw;
  const district = districtRaw;
  const city = cityRaw;

  return { detail, ward, district, city };
}

function stripDiacritics(s: string) {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

function canonicalizeProvinceLabel(input: string) {
  if (!input) return "";
  // Bỏ dấu
  let s = input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  s = s.toLowerCase();

  s = s.replace(/\b(tp\.?|thanh pho|tinh|thu do)\b/g, " "); // bỏ tiền tố hành chính
  s = s.replace(/\b(hcm|hcmc|sai gon|sg)\b/g, "ho chi minh"); // TP.HCM
  s = s.replace(/[^a-z0-9\s]/g, " "); // bỏ ký tự đặc biệt
  s = s.replace(/\s+/g, " ").trim(); // gộp khoảng trắng

  return s;
}

function canonicalizeDistrictWardLabel(s: string) {
  s = stripDiacritics(s).toLowerCase();
  s = s.replace(
    /\b(quan|q\.?|huyen|h\.?|thi xa|tx|thanh pho|tp|phuong|p\.?|xa|x\.?|thi tran|tt)\b/g,
    " "
  );
  s = s
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  s = s.replace(/\b0+(\d+)\b/g, "$1");
  return s;
}

export function getValueByLabel(
  options: OptionType[],
  label: string,
  level: "province" | "district" | "ward"
): OptionType["value"] | undefined {
  if (!label) return undefined;

  const trimmed = label.trim();

  // 1) Exact match
  const exact = options.find((o) => o.label === trimmed);
  if (exact) return exact.value;

  // 2) Case-insensitive
  const lower = trimmed.toLowerCase();
  const ci = options.find((o) => o.label.trim().toLowerCase() === lower);
  if (ci) return ci.value;

  // 3) Canonical (bỏ dấu + bỏ tiền tố)
  const canon =
    level === "province"
      ? canonicalizeProvinceLabel(trimmed)
      : canonicalizeDistrictWardLabel(trimmed);

  const canonMatch = options.find((o) => {
    const oCanon =
      level === "province"
        ? canonicalizeProvinceLabel(o.label)
        : canonicalizeDistrictWardLabel(o.label);
    return oCanon === canon;
  });

  if (canonMatch) return canonMatch.value;

  const contains = options.find((o) => {
    const oCanon =
      level === "province"
        ? canonicalizeProvinceLabel(o.label)
        : canonicalizeDistrictWardLabel(o.label);
    return oCanon.includes(canon) || canon.includes(oCanon);
  });

  return contains?.value;
}

export function mapDoctorsToOptions(doctors: DoctorDto[]): SelectOption<string>[] {
  return doctors.map((d) => ({
    value: d.doctorId ?? "",
    label: d.name ?? "",
  }));
}

export function mapClinicsToOptions(clinics: ClinicScheduleStatusDto[]): SelectOption<number>[] {
  return clinics.map((d) => ({
    value: d.clinicId ?? 0,
    label: d.clinicName ?? "",
  }));
}